import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { SFX_CREDITS, SFX_LICENSES, SFX_SOURCE } from '../../game/credits.js';

/**
 * @file `/credits` — where the sounds came from.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERYTHING ON THIS PAGE IS DERIVED FROM `src/game/credits.js`
 * ─────────────────────────────────────────────────────────────────────────────
 * That file is GENERATED by `npm run sfx` and is regenerated in full every time
 * the pack changes, so **not one author, title, filename or count is typed
 * here**. The section headings are the SLUGS with their underscores knocked
 * out; the totals are sums; the licence summary is `SFX_LICENSES` counted
 * against the entries. A hand-typed name would be the one thing on the page
 * that could go stale without the build noticing, and this page exists
 * precisely so that nothing goes stale without the build noticing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ ONE ROW IS A LICENCE CONDITION AND THE REST ARE A COURTESY
 * ─────────────────────────────────────────────────────────────────────────────
 * 29 of the 30 sources are CC0, which waives the attribution requirement
 * outright — crediting them is manners, not law. Exactly one clip is CC BY 4.0,
 * where the credit is a term of the licence and removing it makes the shipped
 * build non-compliant.
 *
 * The page therefore says that difference OUT LOUD in three places rather than
 * flattening it into one undifferentiated wall:
 *   1. a REQUIRED callout above the list, which reprints the affected entries
 *      in full so a reader who never scrolls still has the legally load-bearing
 *      text in front of them;
 *   2. a siren rule + `REQUIRED` chip on the row itself, in its slug's section,
 *      so someone trimming the list in place can see which row may not go;
 *   3. a plain sentence admitting the CC0 rows are here by choice.
 * Faking the distinction the other way — badging all 30 as "required" for
 * tidiness — would be worse than useless: it makes the one real obligation
 * invisible again, which is the failure this markup exists to prevent.
 *
 * Chrome is `MenuRoute`'s: same two backdrop layers, same `max-w-4xl` column,
 * same `@theme` tokens, same quiet footer chip for the way home. It is a page,
 * not a HUD overlay, so it scrolls — checked at 1280×720 and at 390 px wide.
 */

/** Turns a generated slug into a section heading. Pure transform, no table. */
function slugLabel(slug) {
  return String(slug).replace(/_/g, ' ');
}

/**
 * Groups the flat credit list by slug, keeping first-appearance order — which
 * is the order `tools/build-sfx.mjs` emits, i.e. roughly the order the player
 * meets the sounds in. Several slugs carry three or four separate sources
 * (`footstep` alone is three recordings from two people), and that grouping is
 * the whole difference between a readable page and thirty identical rows.
 */
function groupBySlug(entries) {
  /** @type {Array<{slug: string, rows: any[], files: number}>} */
  const out = [];
  /** @type {Map<string, any>} */
  const bySlug = new Map();
  for (const e of entries) {
    let g = bySlug.get(e.slug);
    if (!g) {
      g = { slug: e.slug, rows: [], files: 0 };
      bySlug.set(e.slug, g);
      out.push(g);
    }
    g.rows.push(e);
    g.files += e.files.length;
  }
  return out;
}

export default function CreditsRoute() {
  const { groups, required, totals } = useMemo(() => {
    const entries = SFX_CREDITS ?? [];
    /** @type {Record<string, number>} */
    const perLicense = {};
    let files = 0;
    for (const e of entries) {
      perLicense[e.license] = (perLicense[e.license] ?? 0) + 1;
      files += e.files.length;
    }
    return {
      groups: groupBySlug(entries),
      required: entries.filter((e) => e.requiresAttribution),
      totals: { entries: entries.length, files, perLicense },
    };
  }, []);

  return (
    <div className="relative h-full w-full overflow-auto bg-void text-hud">
      {/* Same two backdrop layers as the title screen — pure CSS, no asset. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 8%, rgba(255,193,10,0.10) 0%, rgba(7,8,12,0) 55%), radial-gradient(90% 70% at 50% 100%, rgba(45,226,230,0.07) 0%, rgba(7,8,12,0) 60%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(115deg, #ffffff 0 2px, transparent 2px 16px)',
        }}
      />

      <div className="relative mx-auto flex min-h-full max-w-4xl flex-col px-5 py-10 sm:px-6 sm:py-14">
        <header className="text-center">
          <h1 className="gta-title select-none font-display text-[13vw] leading-[0.92] sm:text-6xl">
            CREDITS
          </h1>
          <p className="mt-6 font-hud text-[12px] uppercase tracking-[0.38em] text-hud-dim">
            Sound effects
          </p>
        </header>

        {/* ── where the pack came from, stated once, from SFX_SOURCE ─────── */}
        <section className="mt-10 rounded-[3px] border border-hairline bg-ink/70 px-4 py-4 sm:px-6 sm:py-5">
          <h2 className="font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint">
            Source
          </h2>
          <p className="mt-2 font-hud text-[14px] leading-relaxed text-hud">
            Every sound effect in this game came from{' '}
            <ExtLink href={SFX_SOURCE.url}>{SFX_SOURCE.site}</ExtLink>.
          </p>
          <p className="mt-1.5 font-hud text-[13px] leading-relaxed text-hud-dim">
            {SFX_SOURCE.note}
          </p>
          <p className="mt-3 font-hud text-[12.5px] leading-relaxed text-hud-faint">
            {totals.entries} source recordings · {totals.files} shipped clips ·{' '}
            {SFX_LICENSES.map((lic, i) => (
              <span key={lic}>
                {i > 0 ? ' · ' : ''}
                {totals.perLicense[lic] ?? 0} × {lic}
              </span>
            ))}
          </p>
        </section>

        {/* ── ⚠ THE ONE ROW THAT IS NOT OPTIONAL ──────────────────────────
            Reprinted in full above the list, so the legally load-bearing text
            is on screen for a reader who never scrolls. Rendered from the same
            entries as the list below, so it can never disagree with it — and
            absent entirely if a future pack happens to be all-CC0. */}
        {required.length > 0 ? (
          <section className="mt-6 rounded-[3px] border-2 border-siren/70 bg-siren/[0.07] px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="font-display text-lg uppercase tracking-wide text-siren">
                Attribution required
              </h2>
              <span className="font-hud text-[11px] uppercase tracking-[0.24em] text-hud-faint">
                {required.length} of {totals.entries} sources
              </span>
            </div>
            <p className="mt-2 font-hud text-[13px] leading-relaxed text-hud-dim">
              These credits are a CONDITION of the licence, not a courtesy. If
              this page is ever trimmed, these are the rows that may not go —
              removing them makes the shipped build non-compliant.
            </p>
            <ul className="mt-3 flex flex-col gap-2">
              {required.map((e) => (
                <li key={`req-${e.slug}-${e.files[0]}`}>
                  <CreditRow e={e} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* The honest counterpart: say plainly that the rest are here by choice,
            or the page implies 30 obligations and the real one disappears into
            them. */}
        <p className="mt-6 font-hud text-[13px] leading-relaxed text-hud-dim">
          Everything else below is <strong className="font-bold text-hud">CC0 1.0</strong>, a
          full waiver — those authors ask for nothing and are credited here
          because they should be, not because they must be.
        </p>

        {/* ── the full list, grouped by the sound it makes ─────────────────── */}
        <div className="mt-8 flex flex-col gap-7">
          {groups.map((g) => (
            <section key={g.slug}>
              <h2 className="mb-2 flex flex-wrap items-baseline gap-x-3 border-b border-hairline pb-1.5">
                <span className="font-display text-[17px] uppercase tracking-wide text-gold">
                  {slugLabel(g.slug)}
                </span>
                <span className="font-hud text-[10.5px] uppercase tracking-[0.24em] text-hud-faint">
                  {g.rows.length} {g.rows.length === 1 ? 'source' : 'sources'} · {g.files}{' '}
                  {g.files === 1 ? 'clip' : 'clips'}
                </span>
              </h2>
              <ul className="flex flex-col gap-1.5">
                {g.rows.map((e) => (
                  <li key={`${e.slug}-${e.files[0]}`}>
                    <CreditRow e={e} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="mt-auto w-full pt-14">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/"
              onMouseDown={(e) => e.preventDefault()}
              className="rounded-[3px] border border-hairline/70 px-3 py-1.5 font-hud text-[12px] uppercase tracking-[0.14em] text-hud-faint transition hover:border-neon/60 hover:text-neon"
            >
              ← Back to menu
            </Link>
          </div>
          <p className="mt-6 text-center font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint/70">
            Textures CC0 ambientCG · Fonts Bowlby One SC + Barlow Condensed, OFL 1.1
          </p>
        </footer>
      </div>
    </div>
  );
}

/**
 * ONE credit. The same component in the REQUIRED callout and in the slug list,
 * so the two renderings of the same entry cannot drift apart — the callout is a
 * filter over the list, not a second copy of it.
 *
 * The row's left rule is the state: siren where attribution is a licence
 * condition, hairline where it is manners. `requiresAttribution` is the only
 * thing that decides it; nothing here matches on the licence STRING, so a pack
 * that arrives tomorrow carrying CC BY-SA or CC BY 3.0 is marked correctly by
 * the generator without this file learning a new name.
 *
 * @param {{e: import('../../game/credits.js').CreditEntry}} props
 */
function CreditRow({ e }) {
  const must = e.requiresAttribution;
  return (
    <div
      className={`relative overflow-hidden rounded-[3px] border px-3.5 py-2.5 sm:px-4 ${
        must ? 'border-siren/60 bg-siren/[0.06]' : 'border-hairline bg-ink/60'
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${must ? 'bg-siren' : 'bg-hairline'}`}
      />

      <div className="pl-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <ExtLink href={e.url} className="font-hud text-[14px] font-bold leading-snug text-hud">
            {e.title}
          </ExtLink>
          {must ? (
            <span className="shrink-0 rounded-[2px] border border-siren px-1.5 py-px font-hud text-[9.5px] font-bold uppercase tracking-[0.2em] text-siren">
              Required
            </span>
          ) : null}
        </div>

        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-hud text-[12.5px] text-hud-dim">
          <span>
            by <span className="text-hud">{e.author}</span>
          </span>
          <span aria-hidden="true" className="text-hud-faint">
            ·
          </span>
          <ExtLink
            href={e.licenseUrl}
            className={must ? 'font-bold text-siren' : 'text-hud-faint'}
          >
            {e.license}
          </ExtLink>
        </div>

        {/* The shipped filenames. This is the join back to `public/audio/`, and
            it is what makes the page auditable: every file in that directory
            has a row here and the build throws otherwise. Wraps and breaks on
            a narrow screen rather than forcing a horizontal scroll. */}
        <p className="mt-1 break-words font-mono text-[10.5px] leading-relaxed text-hud-faint">
          {e.files.join('  ')}
        </p>
      </div>
    </div>
  );
}

/**
 * Outbound link. `noreferrer` as well as `noopener` — the second is implied by
 * `target="_blank"` in every current browser, the first is the one that still
 * has to be asked for.
 * @param {{href: string, className?: string, children: any}} props
 */
function ExtLink({ href, className = '', children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onMouseDown={(e) => e.preventDefault()}
      className={`underline decoration-hairline underline-offset-2 transition hover:decoration-neon hover:text-neon ${className}`}
    >
      {children}
    </a>
  );
}
